import "./leafletSetup"; // Leaflet CSS + marker-icon bootstrap (moved out of the eager entry)
/* eslint-disable react/prop-types */
/**
 * Leaflet map controls, base/overlay layer switcher, location search, and a
 * locate-me button. Ported from web_admin's MapLayers.tsx to plain JSX.
 *
 * Two tile sets, picked with the `tiles` prop:
 *  - "google" (default): Google Maps tiles through our backend (the key never
 *    reaches the browser), with Google search and the Google logo + data
 *    credits Google's terms require.
 *  - "open": the Esri / OpenStreetMap layers with OpenStreetMap search. Used by
 *    the maps where users draw boundaries (Survey, Measure, Crowd Data): Google
 *    forbids tracing over its imagery, and its results may not be shown on a
 *    non-Google map, so the two sets never mix on one map.
 *
 * Drop `<MapLayers />` as a child of any react-leaflet <MapContainer>. Use
 * `<MapTileLayers />` when you only want the base layer + switcher (no search /
 * locate chrome), e.g. inside small read-only preview maps.
 */
import { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import {
  readMapBase,
  writeMapBase,
  FlyToOnBroadcast,
  SearchedPlaceDot,
} from "./mapViewStore";
import { getBrowserLocation } from "../../utils/geolocation";
import {
  Search,
  LocateFixed,
  X,
  Loader2,
  Layers,
  Map as MapIcon,
  Satellite,
  Waves,
} from "lucide-react";
import ReactDOM from "react-dom";
import { geoApi, googleTileUrl } from "../../utils/geoApi";
import { newSearchSession, resolvePlace, searchPlaces } from "../../utils/geocoding";
import googleLogo from "../../../assets/maps/google-logo.png";
import { hasSurveyLayerChoice } from "../../utils/surveyLayerPref";

// ── Tile definitions ────────────────────────────────────────────────────────
const BASE_TILES = [
  {
    name: "Street",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    bg: "#e2e8f0",
    dot: "#64748b",
  },
  {
    name: "Satellite",
    // {s} = "server" | "services": both serve the same imagery, and they speak
    // HTTP/1.1 only, so a browser fetches 6 tiles at a time per host. Two hosts
    // double that; one host queued a zoom's tiles for ~3.5 s.
    url: "https://{s}.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
    bg: "#0f4c29",
    dot: "#22c55e",
  },
  {
    name: "Terrain",
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    bg: "#d8f0cc",
    dot: "#65a30d",
  },
  {
    name: "Cadastral",
    // Esri Light Gray Canvas — free/keyless, clean grey (CARTO now needs a key).
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
    bg: "#f8fafc",
    dot: "#94a3b8",
  },
  {
    name: "Dark",
    // Esri Dark Gray Canvas — free/keyless dark base.
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
    bg: "#1e293b",
    dot: "#475569",
  },
];

const OVERLAY_TILES = [
  {
    name: "Labels",
    url: "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    attribution: "Labels &copy; Esri",
    opacity: 0.8,
  },
  {
    name: "Roads",
    url: "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}",
    attribution: "Roads &copy; Esri",
    opacity: 0.7,
  },
];

// Each source only serves tiles up to a certain native zoom; beyond it Leaflet
// upscales the last tiles (blurry) instead of showing blank/white. Without this,
// zooming in past ~18 gives white space.
const MAX_NATIVE = { Street: 19, Satellite: 19, Terrain: 17, Cadastral: 16, Dark: 16 };
// The map itself goes to zoom 20 (Google's sharp limit, see GOOGLE_MAX_NATIVE);
// these Esri layers stop at their native level and are enlarged beyond it, so
// they never turn blank when a map is zoomed further in.
const tileOpts = (name) => ({ maxNativeZoom: MAX_NATIVE[name] ?? 19, maxZoom: 20 });

// Google set. The names stay "Satellite" / "Cadastral" so the one-tap toggle,
// callers' `defaultBase` and the remembered hub choice keep working; `label` is
// what the user sees. Satellite and terrain carry Google's road + place labels.
const GOOGLE_BASE_TILES = [
  { name: "Satellite", label: "Satellite", mapType: "satellite", bg: "#0f4c29", dot: "#22c55e" },
  { name: "Cadastral", label: "Map", mapType: "roadmap", bg: "#f8fafc", dot: "#94a3b8" },
  { name: "Terrain", label: "Terrain", mapType: "terrain", bg: "#d8f0cc", dot: "#65a30d" },
];
// Zoom levels with real Google imagery, checked against Google's own
// per-area limits (Map Tiles viewport `maxZoomRects`) over Hyderabad and rural
// Rangareddy: satellite and road map go to 20 (some outer areas 19); terrain
// stops at 15. Past those Google only serves enlarged, blurry copies, so the
// map lets users zoom to 20 (a single plot fills the screen) and no further.
const GOOGLE_MAX_NATIVE = { Satellite: 20, Cadastral: 20, Terrain: 15 };
const GOOGLE_MAX_ZOOM = 20;

const tileSet = (tiles) =>
  tiles === "open"
    ? { bases: BASE_TILES, overlays: OVERLAY_TILES }
    : { bases: GOOGLE_BASE_TILES, overlays: [] };

/**
 * Google tiles come from our backend (the key stays there). The backend only
 * serves pages on jaaga.ai, which it tells from the Referer, so the policy is
 * set explicitly rather than left to a page-wide default.
 */
const GoogleTileLayer = L.TileLayer.extend({
  createTile(coords, done) {
    const tile = document.createElement("img");
    L.DomEvent.on(tile, "load", L.Util.bind(this._tileOnLoad, this, done, tile));
    L.DomEvent.on(tile, "error", L.Util.bind(this._tileOnError, this, done, tile));
    tile.alt = "";
    tile.setAttribute("role", "presentation");
    tile.referrerPolicy = "strict-origin-when-cross-origin";
    tile.src = googleTileUrl(this.options.mapType, this._getZoomForUrl(), coords.x, coords.y);
    return tile;
  },
});

/**
 * Esri imagery. For a level it has no photo at (rural India often stops at
 * zoom 18) Esri sends a grey "Map data not yet available" tile, so zooming in
 * turned the map grey. With blankTile=false it sends a 404 instead, and this
 * layer then shows the deepest real tile above, enlarged — Leaflet's
 * maxNativeZoom, decided per tile.
 */
const ESRI_FALLBACK_LEVELS = 4;
const EsriImageryLayer = L.TileLayer.extend({
  createTile(coords, done) {
    const tile = document.createElement("div");
    tile.style.overflow = "hidden";
    const img = document.createElement("img");
    img.alt = "";
    img.setAttribute("role", "presentation");
    img.style.position = "absolute";
    tile.appendChild(img);

    const size = this.getTileSize();
    const z = this._getZoomForUrl();
    let up = 0;
    const show = () => {
      const k = 2 ** up;
      img.style.width = `${size.x * k}px`;
      img.style.height = `${size.y * k}px`;
      img.style.left = `${-(coords.x % k) * size.x}px`;
      img.style.top = `${-(coords.y % k) * size.y}px`;
      img.src = L.Util.template(this._url, {
        s: this._getSubdomain(coords),
        x: coords.x >> up,
        y: coords.y >> up,
        z: z - up,
      });
    };
    // Leaflet's TileLayer cancels, on every zoom, any old tile whose
    // `complete` is false — written for <img> tiles. This tile is a <div>
    // (undefined `complete`), so every zoom tore out the whole previous level
    // and the map went white until the new tiles arrived. Flag it like an img.
    tile.complete = false;
    img.onload = () => {
      tile.complete = true;
      done(null, tile);
    };
    img.onerror = () => {
      if (up < ESRI_FALLBACK_LEVELS && z - up > 0) {
        up += 1;
        show();
      } else {
        tile.complete = true;
        done(new Error("No imagery"), tile);
      }
    };
    show();
    return tile;
  },
});

function createBaseLayer(def) {
  if (def.name === "Satellite" && !def.mapType) {
    return new EsriImageryLayer(`${def.url}?blankTile=false`, {
      attribution: def.attribution,
      subdomains: ["server", "services"],
      ...tileOpts(def.name),
      // Sharp screens (Retina laptops, phones, most projectors) get four
      // half-size tiles from one level deeper in place of each tile, the way
      // the PDF report renders its imagery at 2×; at 1× they showed every photo
      // pixel twice as large and the map looked soft next to the report. The
      // URL zoom is one past the map's, so the native cap drops by one to keep
      // requests at Esri's zoom 19 (deeper ones fall back per tile anyway).
      detectRetina: true,
      ...(L.Browser.retina ? { maxNativeZoom: (MAX_NATIVE[def.name] ?? 19) - 1 } : {}),
    });
  }
  if (def.mapType) {
    return new GoogleTileLayer("", {
      mapType: def.mapType,
      maxNativeZoom: GOOGLE_MAX_NATIVE[def.name] ?? 20,
      maxZoom: GOOGLE_MAX_ZOOM,
      // Google bills every tile. By default Leaflet also loads the tiles of each
      // in-between zoom level while a zoom animates; a search pick that flies
      // from zoom 9 to 16 then fetched ~190 tiles instead of ~12. Load only the
      // level the map settles on.
      updateWhenZooming: false,
    });
  }
  return L.tileLayer(def.url, { attribution: def.attribution, ...tileOpts(def.name) });
}

// ── Google logo + data credits (required on every Google map) ───────────────
// Bottom-left, logo then credits ("Map data ©2026 Google, Maxar …"): the
// bottom-right corner is covered by the site's floating WhatsApp button, and
// Google's terms need both to stay visible. The credits come from Google for
// the area in view, so they are re-read after every pan/zoom. Kept apart from
// Leaflet's own attribution.
function GoogleAttribution({ mapType }) {
  const map = useMap();
  const [credit, setCredit] = useState("");
  // "esri" once today's Google tile ceiling is spent and the backend serves
  // free Esri tiles instead: then the Google logo must not be shown.
  const [provider, setProvider] = useState("google");
  // Narrow maps (phones) can't fit the full credit line next to the logo
  // without running into the map's own bottom controls, so it collapses to
  // "Map data ©Google ⓘ" and opens on tap — the "data sources" control Google's
  // attribution rules allow when the full text doesn't fit.
  const [narrow, setNarrow] = useState(() => map.getSize().x < 600);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const onResize = () => setNarrow(map.getSize().x < 600);
    map.on("resize", onResize);
    return () => map.off("resize", onResize);
  }, [map]);

  useEffect(() => {
    let timer;
    let ctrl;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        ctrl?.abort();
        ctrl = new AbortController();
        const b = map.getBounds();
        try {
          const out = await geoApi.viewport(
            mapType,
            {
              zoom: map.getZoom(),
              north: b.getNorth(),
              south: b.getSouth(),
              east: Math.min(180, b.getEast()),
              west: Math.max(-180, b.getWest()),
            },
            { signal: ctrl.signal }
          );
          setCredit(out?.copyright || "");
          setProvider(out?.provider === "esri" ? "esri" : "google");
        } catch {
          /* keep the last credit */
        }
      }, 400);
    };
    refresh();
    map.on("moveend", refresh);
    return () => {
      clearTimeout(timer);
      ctrl?.abort();
      map.off("moveend", refresh);
    };
  }, [map, mapType]);

  const container = useLeafletControl("bottomleft");
  if (!container) return null;
  const esri = provider === "esri";
  const text = esri ? credit : credit ? `Map data ${credit}` : "Map data ©Google";
  return ReactDOM.createPortal(
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 10,
        margin: "0 0 5px 0",
        pointerEvents: "none",
      }}
    >
      {!esri && (
        <img
          src={googleLogo}
          alt="Google Maps"
          aria-label="Google Maps"
          style={{ height: 19, width: "auto", display: "block" }}
        />
      )}
      {narrow && !expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="Show map data sources"
          style={{
            padding: "0 6px",
            background: "rgba(255,255,255,0.75)",
            border: "none",
            fontSize: 10,
            lineHeight: "16px",
            color: "#333",
            cursor: "pointer",
            pointerEvents: "auto",
          }}
        >
          {esri ? "Tiles ©Esri" : "Map data ©Google"} ⓘ
        </button>
      ) : (
        <span
          title={text}
          onClick={narrow ? () => setExpanded(false) : undefined}
          style={{
            padding: "0 6px",
            background: "rgba(255,255,255,0.9)",
            fontSize: 10,
            lineHeight: "16px",
            color: "#333",
            maxWidth: narrow ? "calc(100vw - 110px)" : "min(60vw, 420px)",
            whiteSpace: narrow ? "normal" : "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            pointerEvents: "auto",
            cursor: narrow ? "pointer" : "default",
          }}
        >
          {text}
        </span>
      )}
    </div>,
    container
  );
}

// ── Hook: proper Leaflet control container via React portal ──────────────────
// Must use the "leaflet-control" class so the div gets pointer-events:auto
// (the .leaflet-top/.leaflet-bottom parents have pointer-events:none). Do NOT
// disableClickPropagation, it stops native bubbling and breaks React 18's
// delegated event listeners (which sit on #root).
function useLeafletControl(position) {
  const map = useMap();
  const [div, setDiv] = useState(null);

  useEffect(() => {
    const container = L.DomUtil.create("div", "leaflet-control");
    const ctrl = new L.Control({ position });
    ctrl.onAdd = () => container;
    ctrl.addTo(map);
    L.DomEvent.disableScrollPropagation(container);
    // Stop drag-start + double-click from bubbling to the map, so interacting
    // with a control (e.g. the base-map toggle) can't start a map drag or fire
    // Leaflet's built-in double-click zoom (which made rapid base switches zoom
    // the map in). We deliberately do NOT stop `click`, React 18 delegates
    // click handling at the root, so stopping it here would break the buttons.
    L.DomEvent.on(
      container,
      "mousedown dblclick touchstart",
      L.DomEvent.stopPropagation
    );
    setDiv(container);
    return () => {
      ctrl.remove();
    };
  }, [map, position]);

  return div;
}

// ── Mobile-style base toggle button (Cadastral ⇄ Satellite) ──────────────────
// Bound with a NATIVE click listener + Leaflet's disableClickPropagation so a tap
// here is fully isolated from the map: it can never reach the map's click / zoom
// handlers, so flipping the base map never zoom-to-clicks or double-click zooms
// (the bug where switching satellite⇄cadastral zoomed the survey/crowd maps in).
// A React onClick can't be isolated this way, React delegates clicks at the
// root, below the map's own listener, so the map would still receive the click.
function BaseToggleButton({ onSatellite, onToggle }) {
  const ref = useRef(null);
  const cb = useRef(onToggle);
  cb.current = onToggle;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
    const handler = (e) => {
      L.DomEvent.stop(e);
      cb.current();
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, []);
  const Icon = onSatellite ? MapIcon : Satellite;
  return (
    <button
      ref={ref}
      type="button"
      title={onSatellite ? "Map view" : "Satellite view"}
      style={{
        width: 34,
        height: 34,
        background: "white",
        border: "2px solid rgba(0,0,0,0.18)",
        borderRadius: 8,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 1px 5px rgba(0,0,0,0.25)",
      }}
    >
      <Icon size={18} style={{ color: "#153568" }} />
    </button>
  );
}

// ── Layer switcher UI ───────────────────────────────────────────────────────
// Survey-number layer button + a one-line nudge beside it. The layer is off
// until the user turns it on; their choice is remembered for every map and
// visit (surveyLayerPref). The nudge only shows to someone who has never made
// a choice, hides itself after a while, and never returns once they enable the
// layer, turn it off, or close the nudge.
const SURVEY_HINT_KEY = "jg_survey_hint_done";
function SurveyToggle({ active, onToggle }) {
  const [hint, setHint] = useState(() => {
    if (active || hasSurveyLayerChoice()) return false;
    try {
      return !localStorage.getItem(SURVEY_HINT_KEY);
    } catch {
      return true;
    }
  });
  const done = () => {
    setHint(false);
    try {
      localStorage.setItem(SURVEY_HINT_KEY, "1");
    } catch {
      /* private mode: the nudge may show again next visit */
    }
  };
  useEffect(() => {
    if (!hint) return undefined;
    const t = setTimeout(() => setHint(false), 15000);
    return () => clearTimeout(t);
  }, [hint]);
  useEffect(() => {
    if (active && hint) done();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
      {hint && !active && (
        <div
          role="status"
          style={{
            position: "absolute",
            right: 52,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "max-content",
            maxWidth: "min(300px, calc(100vw - 96px))",
            lineHeight: 1.3,
            background: "#153568",
            color: "white",
            borderRadius: 10,
            padding: "6px 6px 6px 12px",
            fontSize: 12,
            fontWeight: 600,
            boxShadow: "0 6px 18px rgba(6,31,72,0.35)",
          }}
        >
          Enable survey numbers to view them on the map
          <button
            onClick={() => {
              if (!active) onToggle();
              done();
            }}
            style={{
              flexShrink: 0,
              background: "#f0a500",
              color: "#153568",
              border: "none",
              borderRadius: 7,
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Enable
          </button>
          <button
            onClick={done}
            aria-label="Dismiss"
            style={{
              background: "transparent",
              color: "rgba(255,255,255,0.75)",
              border: "none",
              cursor: "pointer",
              lineHeight: 0,
              padding: 2,
            }}
          >
            <X size={13} />
          </button>
          {/* arrow pointing at the button */}
          <span
            style={{
              position: "absolute",
              right: -5,
              top: "50%",
              width: 10,
              height: 10,
              background: "#153568",
              transform: "translateY(-50%) rotate(45deg)",
            }}
          />
        </div>
      )}
      <button
        onClick={onToggle}
        title={active ? "Hide survey numbers" : "Show survey numbers"}
        aria-label={active ? "Hide survey numbers" : "Show survey numbers"}
        aria-pressed={active}
        style={{
          minWidth: 44,
          padding: "0 6px",
          height: 34,
          background: active ? "#153568" : "white",
          border: `2px solid ${active ? "#153568" : "rgba(0,0,0,0.18)"}`,
          borderRadius: 8,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 5px rgba(0,0,0,0.25)",
        }}
      >
        {/* Text, not a pictogram: Indian maps and records call these "Sy. No."
            and no icon (flag-on-plot, grid, #) reads as survey numbers. */}
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: active ? "white" : "#153568",
            lineHeight: 1,
          }}
        >
          Sy.No
        </span>
      </button>
    </div>
  );
}

function LayerSwitcherUI({
  extraOverlays = [],
  activeExtras = [],
  onToggleExtra,
  // Which base layer boots first. Every map in the app opens on Satellite;
  // callers can still pass another name from BASE_TILES.
  defaultBase = "Satellite",
  // When true, render the mobile-style one-tap Cadastral⇄Satellite toggle button
  // instead of the full base-layer dropdown (matches the Flutter app).
  baseToggle = false,
  // Maps-hub only: remember the chosen base across tab switches / reloads, so a
  // satellite view sticks instead of snapping back to `defaultBase` on remount.
  persistBase = false,
  // One overlay promoted OUT of the Overlays popover into the button stack
  // itself, for a layer that is on by default and toggled often enough that a
  // nested tap is a nuisance (My Property's HYDRA FTL).
  // Shape: { key, name, active, onToggle }.
  primaryExtra,
  // "google" | "open", see the top of this file.
  tiles = "google",
  // Tells the parent which Google map type is showing (for the credits line).
  onMapTypeChange,
}) {
  const map = useMap();
  const { bases: BASE_TILES, overlays: OVERLAY_TILES } = tileSet(tiles);
  const [open, setOpen] = useState(false);
  const [activeBase, setActiveBase] = useState(() => {
    const want = persistBase ? readMapBase(defaultBase) : defaultBase;
    // A remembered name that no longer exists in BASE_TILES falls back to the
    // caller's default, otherwise switchBase couldn't find the layer to remove.
    return BASE_TILES.some((t) => t.name === want) ? want : defaultBase;
  });
  const [activeOverlays, setActiveOverlays] = useState(new Set());
  const layersRef = useRef(new Map());
  // Wraps the layers button + popup, so an outside click can close the popup.
  const rootRef = useRef(null);

  // Boot the base layer the map opens on (the remembered one when persisting,
  // otherwise the caller's default).
  useEffect(() => {
    const base = BASE_TILES.find((t) => t.name === activeBase) || BASE_TILES[0];
    const layer = createBaseLayer(base);
    layer.addTo(map);
    layersRef.current.set(base.name, layer);
    onMapTypeChange?.(base.mapType);
    return () => {
      layer.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the open layers popup when the user clicks/taps anywhere outside this
  // control (after picking their layers). Capture-phase so it fires before the
  // click reaches the map or another control.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  // Neutralise Leaflet's default `.leaflet-control` 10px margins on THIS control's
  // container so our own inner margin ("12px 8px 0 0") is the sole offset. Without
  // this, Leaflet's margin stacks unpredictably and the satellite / map-view +
  // layers icons don't line up at 12px top / 8px right with the other top controls
  // (search, All/Own/Public). Base-toggle (Maps-hub) style only.
  useEffect(() => {
    if (!baseToggle) return;
    const container = rootRef.current?.parentElement;
    if (container) container.style.margin = "0";
  }, [baseToggle]);

  const switchBase = (name) => {
    const prev = layersRef.current.get(activeBase);
    if (prev) {
      prev.remove();
      layersRef.current.delete(activeBase);
    }
    const def = BASE_TILES.find((t) => t.name === name);
    const layer = createBaseLayer(def);
    layer.addTo(map);
    layersRef.current.set(name, layer);
    onMapTypeChange?.(def.mapType);
    setActiveBase(name);
    if (persistBase) writeMapBase(name);
  };

  // One-tap base flip (mobile parity): Satellite (the default) ⇄ Cadastral.
  // When the default IS Satellite, the "map view" side is the clean Cadastral.
  const onSatellite = activeBase === "Satellite";
  const mapBase = defaultBase === "Satellite" ? "Cadastral" : defaultBase;
  const toggleBase = () => switchBase(onSatellite ? mapBase : "Satellite");

  const toggleOverlay = (name) => {
    const def = OVERLAY_TILES.find((t) => t.name === name);
    if (activeOverlays.has(name)) {
      layersRef.current.get(name)?.remove();
      layersRef.current.delete(name);
      setActiveOverlays((prev) => {
        const n = new Set(prev);
        n.delete(name);
        return n;
      });
    } else {
      const layer = L.tileLayer(def.url, {
        attribution: def.attribution,
        opacity: def.opacity,
        maxNativeZoom: 19,
        maxZoom: 20,
      });
      layer.addTo(map);
      layersRef.current.set(name, layer);
      setActiveOverlays((prev) => new Set([...prev, name]));
    }
  };

  // ── Mobile-style base toggle (Cadastral ⇄ Satellite) ──────────────────────
  // A single white square button that flips the base map, exactly like the
  // Flutter app: it shows a satellite icon on the clean/cadastral map (tap →
  // satellite) and a map icon on satellite (tap → back). Overlays (Labels/Roads
  // + any caller extras such as My Property's Lakes/Transport) still get their
  // own compact Layers popover below it, mirroring the app's separate layers
  // panel, shown only when overlays are actually offered.
  if (baseToggle) {
    // Survey Numbers gets its own one-tap button in the stack (like HYDRA FTL)
    // instead of a row in the Overlays popover; the rest stay in the popover.
    const surveyExtra = extraOverlays.find((o) => o.key === "survey");
    const popoverExtras = extraOverlays.filter((o) => o.key !== "survey");
    // Shown only when the caller offers its own overlays (Lakes, Transport …);
    // the Google set has no tile overlays of its own.
    const hasOverlays = popoverExtras.length > 0;
    return (
      <div
        ref={rootRef}
        style={{
          margin: "12px 10px 0 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 6,
        }}
      >
        <BaseToggleButton onSatellite={onSatellite} onToggle={toggleBase} />

        {hasOverlays && (
          <>
            <button
              onClick={() => setOpen((v) => !v)}
              title="Overlays"
              style={{
                width: 34,
                height: 34,
                background: open ? "#153568" : "white",
                border: `2px solid ${open ? "#153568" : "rgba(0,0,0,0.18)"}`,
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 1px 5px rgba(0,0,0,0.25)",
              }}
            >
              <Layers size={16} style={{ color: open ? "white" : "#153568" }} />
            </button>
            {open && (
              <div
                style={{
                  background: "white",
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
                  overflow: "hidden",
                  minWidth: 170,
                }}
              >
                {/* Header carries an explicit close, matching the full
                    Base Layer panel. Without it this popover could only be
                    dismissed by clicking outside or re-tapping the Layers
                    button, which is not obvious on a touch screen — and it
                    covers the map while it stays open. Toggling a row does NOT
                    close it, so several overlays can be switched in one go. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "8px 8px 4px 12px",
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    Overlays
                  </span>
                  <button
                    onClick={() => setOpen(false)}
                    title="Close"
                    aria-label="Close overlays"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "2px 4px",
                      borderRadius: 4,
                      color: "#94a3b8",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <X size={13} />
                  </button>
                </div>
                {[
                  ...OVERLAY_TILES.map((t) => ({
                    key: t.name,
                    name: t.name,
                    active: activeOverlays.has(t.name),
                    onClick: () => toggleOverlay(t.name),
                  })),
                  ...popoverExtras.map((o) => ({
                    key: o.key,
                    name: o.name,
                    active: activeExtras.includes(o.key),
                    onClick: () => onToggleExtra?.(o.key),
                  })),
                ].map((row) => (
                  <button
                    key={row.key}
                    onClick={row.onClick}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "7px 12px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        border: `2px solid ${row.active ? "#2563EB" : "#cbd5e1"}`,
                        background: row.active ? "#2563EB" : "white",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {row.active && (
                        <svg width="8" height="8" viewBox="0 0 8 8">
                          <path
                            d="M1 4l2 2 4-4"
                            stroke="white"
                            strokeWidth="1.5"
                            fill="none"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span style={{ fontSize: 12, color: "#374151" }}>
                      {row.name}
                    </span>
                  </button>
                ))}
                <div style={{ height: 4 }} />
              </div>
            )}
          </>
        )}

        {surveyExtra && (
          <SurveyToggle
            active={activeExtras.includes("survey")}
            onToggle={() => onToggleExtra?.("survey")}
          />
        )}

        {/* Promoted layer toggle, sitting under the base/overlays buttons at the
            same 34px square so the stack reads as one control column. */}
        {primaryExtra && (
          <button
            onClick={() => primaryExtra.onToggle?.(primaryExtra.key)}
            title={primaryExtra.name}
            aria-pressed={!!primaryExtra.active}
            style={{
              width: 34,
              height: 34,
              background: primaryExtra.active ? "#153568" : "white",
              border: `2px solid ${primaryExtra.active ? "#153568" : "rgba(0,0,0,0.18)"}`,
              borderRadius: 8,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 5px rgba(0,0,0,0.25)",
            }}
          >
            <Waves
              size={16}
              style={{ color: primaryExtra.active ? "white" : "#153568" }}
            />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ margin: "8px 0 0 0" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Map layers"
        style={{
          width: 30,
          height: 30,
          background: open ? "#153568" : "white",
          border: `2px solid ${open ? "#153568" : "rgba(0,0,0,0.25)"}`,
          borderRadius: 4,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 5px rgba(0,0,0,0.2)",
          marginLeft: "auto",
        }}
      >
        <Layers size={14} style={{ color: open ? "white" : "#374151" }} />
      </button>

      {open && (
        <div
          style={{
            marginTop: 4,
            background: "white",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
            overflow: "hidden",
            minWidth: 170,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 8px 4px 12px",
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#94a3b8",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Base Layer
            </span>
            <button
              onClick={() => setOpen(false)}
              title="Close"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 4px",
                borderRadius: 4,
                color: "#94a3b8",
                display: "flex",
                alignItems: "center",
              }}
            >
              <X size={13} />
            </button>
          </div>

          {BASE_TILES.map((t) => {
            const active = activeBase === t.name;
            return (
              <button
                key={t.name}
                onClick={() => switchBase(t.name)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "7px 12px",
                  background: active ? "#eff6ff" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    background: t.bg,
                    border: `2px solid ${active ? "#2563EB" : "rgba(0,0,0,0.12)"}`,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: t.dot,
                    }}
                  />
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: active ? "#1d4ed8" : "#374151",
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {t.label || t.name}
                </span>
                {active && (
                  <span
                    style={{
                      marginLeft: "auto",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#2563EB",
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            );
          })}

          {OVERLAY_TILES.length + extraOverlays.length > 0 && (
            <>
              <div style={{ height: 1, background: "#f1f5f9", margin: "4px 0" }} />
              <div
                style={{
                  padding: "4px 12px 4px",
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#94a3b8",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Overlays
              </div>
            </>
          )}

          {OVERLAY_TILES.map((t) => {
            const active = activeOverlays.has(t.name);
            return (
              <button
                key={t.name}
                onClick={() => toggleOverlay(t.name)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "7px 12px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    border: `2px solid ${active ? "#2563EB" : "#cbd5e1"}`,
                    background: active ? "#2563EB" : "white",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {active && (
                    <svg width="8" height="8" viewBox="0 0 8 8">
                      <path
                        d="M1 4l2 2 4-4"
                        stroke="white"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </span>
                <span style={{ fontSize: 12, color: "#374151" }}>{t.name}</span>
              </button>
            );
          })}

          {/* Caller-supplied overlays (e.g. Lakes / HYDRA, Transport). */}
          {extraOverlays.map((o) => {
            const active = activeExtras.includes(o.key);
            return (
              <button
                key={o.key}
                onClick={() => onToggleExtra?.(o.key)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "7px 12px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    border: `2px solid ${active ? "#2563EB" : "#cbd5e1"}`,
                    background: active ? "#2563EB" : "white",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {active && (
                    <svg width="8" height="8" viewBox="0 0 8 8">
                      <path
                        d="M1 4l2 2 4-4"
                        stroke="white"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </span>
                <span style={{ fontSize: 12, color: "#374151" }}>{o.name}</span>
              </button>
            );
          })}

          <div style={{ height: 4 }} />
        </div>
      )}
    </div>
  );
}

function LayerSwitcher({
  extraOverlays,
  activeExtras,
  onToggleExtra,
  defaultBase,
  baseToggle,
  persistBase,
  primaryExtra,
  tiles = "google",
}) {
  const container = useLeafletControl("topright");
  const [mapType, setMapType] = useState(null);
  return (
    <>
      {container &&
        ReactDOM.createPortal(
          <LayerSwitcherUI
            extraOverlays={extraOverlays}
            activeExtras={activeExtras}
            onToggleExtra={onToggleExtra}
            defaultBase={defaultBase}
            baseToggle={baseToggle}
            persistBase={persistBase}
            primaryExtra={primaryExtra}
            tiles={tiles}
            onMapTypeChange={setMapType}
          />,
          container
        )}
      {tiles !== "open" && mapType && <GoogleAttribution mapType={mapType} />}
    </>
  );
}

// ── Locate-me button ────────────────────────────────────────────────────────
const LOCATE_PIN_HTML = `<div style="position:relative;width:24px;height:24px">
  <div style="position:absolute;inset:0;border-radius:50%;background:#2563EB;opacity:0.25;animation:leaflet-pulse 1.5s ease-out infinite"></div>
  <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:12px;height:12px;border-radius:50%;background:#2563EB;border:2.5px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35)"></div>
</div>`;

if (
  typeof document !== "undefined" &&
  !document.getElementById("leaflet-pulse-style")
) {
  const style = document.createElement("style");
  style.id = "leaflet-pulse-style";
  style.textContent = `@keyframes leaflet-pulse{0%{transform:scale(0.5);opacity:0.4}100%{transform:scale(2.5);opacity:0}}`;
  document.head.appendChild(style);
}

// A neighbourhood view for the auto-centre on load (frames the area around the
// user rather than a tight ~50 m box like the manual button's deep locateZoom).
const AUTO_LOCATE_ZOOM = 15;

// Remember the outcome of the session's ONE location ask so the Maps-hub tabs
// (My Property / Survey / Crowd) never prompt twice in a visit. A granted fix is
// reused by every later tab open; a refusal is remembered too, so someone who
// said no isn't asked again on each navigation. The manual "locate me" button is
// an explicit re-ask and always re-fetches, refreshing whatever is stored.
const SESSION_FIX_KEY = "jaaga_maps_user_fix";

// null = not asked yet this session | { lat, lng } = granted | { noFix: true } =
// asked and got nothing back (refused, unavailable, or timed out).
function readSessionOutcome() {
  try {
    const o = JSON.parse(sessionStorage.getItem(SESSION_FIX_KEY));
    if (o?.noFix) return { noFix: true };
    return Number.isFinite(o?.lat) && Number.isFinite(o?.lng)
      ? { lat: o.lat, lng: o.lng }
      : null;
  } catch {
    return null;
  }
}
function writeSessionFix(lat, lng) {
  try {
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      sessionStorage.setItem(SESSION_FIX_KEY, JSON.stringify({ lat, lng }));
    }
  } catch {
    /* sessionStorage unavailable — auto-locate just re-fetches next time */
  }
}
function writeSessionNoFix() {
  try {
    sessionStorage.setItem(SESSION_FIX_KEY, JSON.stringify({ noFix: true }));
  } catch {
    /* sessionStorage unavailable — auto-locate just re-asks next time */
  }
}

function LocateUI({ map, locateZoom, autoLocate = false, autoLocateRecenter = true, autoLocateZoom = AUTO_LOCATE_ZOOM, onLocatingChange, onLocate }) {
  const [locating, setLocating] = useState(false);
  const [denied, setDenied] = useState(false);
  // The blocking overlay is debounced behind `locating` so a fast (cached) fix
  // doesn't flash it — it only appears if locating takes longer than a moment.
  const [showOverlay, setShowOverlay] = useState(false);
  const markerRef = useRef(null);

  // Drop / move the "you are here" pin. Wrapped so a fix that resolves after the
  // map was torn down (a tab switch, or StrictMode's dev remount) can't throw on
  // a removed Leaflet instance. zIndexOffset keeps the dot above property /
  // crowd pins.
  const dropMarker = (lat, lng) => {
    if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    try {
      markerRef.current?.remove();
      markerRef.current = L.marker([lat, lng], {
        icon: L.divIcon({
          className: "",
          html: LOCATE_PIN_HTML,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        }),
        zIndexOffset: 1000,
        interactive: false,
        keyboard: false,
      }).addTo(map);
    } catch {
      /* map already removed */
    }
  };

  const flyToFix = (lat, lng, zoom) => {
    try {
      map.flyTo([lat, lng], Math.min(zoom, map.getMaxZoom()));
    } catch {
      /* map already removed */
    }
  };

  // Bind the locate button with a NATIVE listener + disableClickPropagation,
  // the same way BaseToggleButton does. Without this the tap reaches the map
  // underneath as well: `useLeafletControl` deliberately doesn't stop `click`
  // (React 18 delegates at the root), so on a phone one tap on "my location"
  // ALSO fired the map's click handler — which on My Property opens the
  // Add-Property flow for that spot, and elsewhere selects a marker. That's the
  // "wrong action / double select" on mobile.
  const locateBtnRef = useRef(null);
  const locateCb = useRef(null);
  useEffect(() => {
    const el = locateBtnRef.current;
    if (!el) return undefined;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
    const handler = (e) => {
      L.DomEvent.stop(e);
      locateCb.current?.();
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, []);

  // Manual "my location" button: a fresh precise fix (maximumAge 0 so we never
  // reuse a stale cached reading) that always recenters, at the caller's deep
  // `locateZoom` (or one step in from the current view).
  const handleClick = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    setDenied(false);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const target =
          locateZoom != null
            ? Math.min(locateZoom, map.getMaxZoom())
            : Math.max(map.getZoom(), 15);
        flyToFix(coords.latitude, coords.longitude, target);
        dropMarker(coords.latitude, coords.longitude);
        writeSessionFix(coords.latitude, coords.longitude); // refresh the session fix
        // Tell the caller WHERE, so a picker map can drop its own selectable
        // point here — pressing "my location" on one of those means "use this
        // spot", not just "look at it". Deliberately NOT fired by auto-locate
        // below: arriving on a page must never silently select a location.
        onLocate?.(coords.latitude, coords.longitude);
        setLocating(false);
      },
      () => {
        setLocating(false);
        setDenied(true);
        setTimeout(() => setDenied(false), 3000);
        // Record the refusal so auto-locate stays quiet for the rest of the
        // session — but never downgrade a fix that was already granted.
        if (!readSessionOutcome()) writeSessionNoFix();
      },
      { timeout: 15000, enableHighAccuracy: true, maximumAge: 0 }
    );
  };
  // Keep the native listener above pointed at the latest closure.
  locateCb.current = handleClick;

  // Auto-centre + pin the user's current location when a Maps-hub map mounts.
  // Every hub tab (My Property / Survey / Crowd) drops a "you are here" dot and
  // (unless the caller opts out via `autoLocateRecenter`) flies to it, so opening
  // a map lands on where you are.
  //
  // We ask at most ONCE per browser session. A granted fix is remembered and
  // reused by later tab opens; a refusal (or timeout) is remembered too and stops
  // us re-asking — no repeated geolocation prompt / "Fetching location" freeze on
  // every navigation, whichever way the first ask went. When we do fetch, it's a
  // FRESH, PRECISE fix (maximumAge 0, high accuracy) with no coarse IP fallback —
  // an IP/stale reading is often a different locality, so showing it as "you are
  // here" is worse than briefly showing nothing. The manual "locate me" button
  // stays available as the deliberate re-ask and refreshes what's stored.
  useEffect(() => {
    if (!autoLocate || !map) return undefined;
    // Marks THIS effect run current. StrictMode runs the effect twice (setup →
    // cleanup → setup); a per-run flag lets the stale first run bow out while the
    // live second run applies the fix — instead of a shared ref that gets stuck.
    let active = true;

    // A predicate gets the fix, so a host can recentre only for some places
    // (e.g. the request pop-up on a state page: only inside that state).
    const wantRecenter = (fix) =>
      typeof autoLocateRecenter === "function"
        ? autoLocateRecenter(fix)
        : autoLocateRecenter;

    // Already asked this session — honour that answer without prompting again.
    const outcome = readSessionOutcome();
    if (outcome?.noFix) {
      // They said no (or we got nothing): leave the map as-is and stay quiet.
      return () => {
        active = false;
      };
    }
    if (outcome) {
      dropMarker(outcome.lat, outcome.lng);
      if (wantRecenter(outcome)) flyToFix(outcome.lat, outcome.lng, autoLocateZoom);
      return () => {
        active = false;
      };
    }

    // First ask of the session — fetch a fresh precise fix and remember how it went.
    setLocating(true);
    (async () => {
      const fix = await getBrowserLocation({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });
      if (!active) return;
      setLocating(false);
      if (!fix) {
        // Denied / unavailable — leave the map as-is (no wrong dot) and record it
        // so the other hub tabs don't prompt again this session.
        writeSessionNoFix();
        return;
      }
      writeSessionFix(fix.lat, fix.lng);
      dropMarker(fix.lat, fix.lng);
      if (wantRecenter(fix)) flyToFix(fix.lat, fix.lng, autoLocateZoom);
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      markerRef.current?.remove();
    },
    []
  );

  // Debounce the overlay: show it only if a fix is still pending after a short
  // delay (so a granted, cached fix that resolves instantly never flashes it).
  useEffect(() => {
    if (!locating) {
      setShowOverlay(false);
      return undefined;
    }
    const t = setTimeout(() => setShowOverlay(true), 350);
    return () => clearTimeout(t);
  }, [locating]);

  // Surface the (debounced) locating state to the parent, so it can ignore
  // rail-card clicks while the map fetches the user's location. Reset on unmount
  // so a stale "locating" can't stick.
  useEffect(() => {
    onLocatingChange?.(showOverlay);
  }, [showOverlay, onLocatingChange]);
  useEffect(() => () => onLocatingChange?.(false), [onLocatingChange]);

  return (
    <div style={{ margin: "0 0 8px 0" }}>
      <button
        ref={locateBtnRef}
        title={denied ? "Location access denied" : "My location"}
        style={{
          width: 30,
          height: 30,
          background: denied ? "#fef2f2" : "white",
          border: `2px solid ${denied ? "#fca5a5" : "rgba(0,0,0,0.25)"}`,
          borderRadius: 4,
          cursor: navigator.geolocation ? "pointer" : "not-allowed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 5px rgba(0,0,0,0.2)",
          marginLeft: "auto",
        }}
      >
        {locating ? (
          <Loader2 size={14} className="animate-spin" style={{ color: "#6b7280" }} />
        ) : (
          <LocateFixed size={14} style={{ color: denied ? "#ef4444" : "#374151" }} />
        )}
      </button>
      {showOverlay && <LocatingOverlay map={map} fullPage={autoLocate} />}
    </div>
  );
}

// "Fetching your location…" overlay shown while a geolocation fix is in progress.
// It swallows clicks / drags / scroll (`L.DomEvent.disable*Propagation`), so no
// action can be taken until the fix resolves — the user sees WHY things are busy.
//
// `fullPage` (used by the Maps-hub tabs — My Property / Survey / Crowd) covers the
// WHOLE viewport (portalled to <body>), not just the map, so the entire page is
// frozen. Otherwise it covers only the map container (address-picker maps etc.).
function LocatingOverlay({ map, fullPage = false }) {
  const attach = (node) => {
    if (!node) return;
    L.DomEvent.disableClickPropagation(node);
    L.DomEvent.disableScrollPropagation(node);
  };
  const pill = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 16px",
        borderRadius: 999,
        background: "white",
        boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
        fontSize: 13,
        fontWeight: 600,
        color: "#334155",
      }}
    >
      <Loader2 size={16} className="animate-spin" style={{ color: "#2563EB" }} />
      <span>Fetching your location…</span>
    </div>
  );

  if (fullPage) {
    // Full-page freeze — covers the entire viewport (rail, chrome, everything).
    return ReactDOM.createPortal(
      <div
        ref={attach}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2000, // above the navbar + map controls
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.55)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          cursor: "progress",
        }}
      >
        {pill}
      </div>,
      document.body
    );
  }

  const container = map && map.getContainer ? map.getContainer() : null;
  if (!container) return null;
  return ReactDOM.createPortal(
    <div
      ref={attach}
      style={{
        position: "absolute",
        inset: 0,
        // Above Leaflet's controls (~800–1000) so it blocks them too.
        zIndex: 1500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(255,255,255,0.45)",
        backdropFilter: "blur(1.5px)",
        WebkitBackdropFilter: "blur(1.5px)",
        cursor: "progress",
      }}
    >
      {pill}
    </div>,
    container
  );
}

function LocateControl({ locateZoom, autoLocate, autoLocateRecenter, autoLocateZoom, onLocatingChange, onLocate }) {
  const map = useMap();
  const container = useLeafletControl("bottomright");
  if (!container) return null;
  return ReactDOM.createPortal(
    <LocateUI
      map={map}
      locateZoom={locateZoom}
      autoLocate={autoLocate}
      autoLocateRecenter={autoLocateRecenter}
      autoLocateZoom={autoLocateZoom}
      onLocatingChange={onLocatingChange}
      onLocate={onLocate}
    />,
    container
  );
}

// Zoom +/- as a custom bottom-right control so it sits next to the locate
// button (instead of Leaflet's default top-left). Styled to match the locate /
// layer buttons. Mounted last so it stacks above locate in the bottom corner.
function ZoomControlBR() {
  const map = useMap();
  const container = useLeafletControl("bottomright");
  if (!container) return null;
  const btn = (extra) => ({
    width: 30,
    height: 30,
    background: "white",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#374151",
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1,
    padding: 0,
    ...extra,
  });
  return ReactDOM.createPortal(
    <div
      style={{
        margin: "0 0 8px 0",
        marginLeft: "auto",
        width: 30,
        border: "2px solid rgba(0,0,0,0.25)",
        borderRadius: 4,
        overflow: "hidden",
        boxShadow: "0 1px 5px rgba(0,0,0,0.2)",
        background: "white",
      }}
    >
      <button
        title="Zoom in"
        onClick={() => map.zoomIn()}
        style={btn({ borderBottom: "1px solid rgba(0,0,0,0.12)" })}
      >
        +
      </button>
      <button title="Zoom out" onClick={() => map.zoomOut()} style={btn()}>
        −
      </button>
    </div>,
    container
  );
}

// ── Map search (Nominatim) ───────────────────────────────────────────────────
function SearchUI({ map, onSearchSelect, summary = null, keepZoom = false, provider = "google" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef();
  const ctrl = useRef();
  const markerRef = useRef(null);
  // One Google autocomplete session per search, closed by the pick.
  const session = useRef(null);

  // Typing only fills the box (and offers a highlighted "Search for …" row);
  // Google is asked when the user presses Enter, taps the search icon or that
  // row — one lookup per search instead of one per pause in typing.
  const [searchedFor, setSearchedFor] = useState("");
  const search = (q) => {
    setQuery(q);
    clearTimeout(timer.current);
    ctrl.current?.abort();
    setResults([]);
    setSearchedFor("");
    setOpen(q.trim().length >= 3);
  };

  const runSearch = (raw) => {
    const q = (raw ?? query).trim();
    if (q.length < 3) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      ctrl.current?.abort();
      ctrl.current = new AbortController();
      if (!session.current) session.current = newSearchSession();
      const c = map.getCenter();
      const data = await searchPlaces(q, {
        limit: 10,
        provider,
        session: session.current,
        near: { lat: c.lat, lng: c.lng },
        signal: ctrl.current.signal,
      });
      setResults(data);
      setSearchedFor(q);
      setOpen(true);
      setLoading(false);
    }, 0);
  };
  const promptSearch = query.trim().length >= 3 && searchedFor !== query.trim();

  const select = async (picked) => {
    // Stamped before the await: the stray map click described below fires right
    // after this mousedown, while the place is still being resolved.
    map._jaagaSearchSelectAt = Date.now();
    setLoading(true);
    const r = await resolvePlace(picked, { session: session.current });
    session.current = null;
    setLoading(false);
    if (!r) return;
    const latlng = L.latLng(Number(r.lat), Number(r.lng));
    // Normally the search zooms in to ~street level (16). When `keepZoom` is set
    // (e.g. the Crowd Data map) we ONLY pan to the place, panTo can never change
    // the zoom, so searching a location never auto-zooms the map.
    if (keepZoom) map.panTo(latlng);
    else map.flyTo(latlng, 16);
    // Picking a result removes its button on mousedown, so the browser fires the
    // trailing `click` on the MAP container itself (not on a `.leaflet-control`).
    // Stamp the time so the map's ClickHandler can ignore that stray click,
    // otherwise, on a zoom-gated map (Crowd: zoomToClick + minPickZoom), the
    // stray click flies to the gate zoom (16) and overrides the pan above.
    map._jaagaSearchSelectAt = Date.now();
    onSearchSelect?.({
      lat: Number(r.lat),
      lng: Number(r.lng),
      display_name: r.display_name,
      // The place's extent, so a picker map can outline the area it flew to.
      boundingbox: r.raw?.boundingbox,
    });
    markerRef.current?.remove();
    markerRef.current = L.marker(latlng, {
      icon: L.divIcon({
        className: "",
        html: `<div style="width:12px;height:12px;border-radius:50%;background:#2563EB;border:3px solid white;box-shadow:0 0 0 3px rgba(37,99,235,0.35)"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
      }),
    }).addTo(map);
    setQuery(r.display_name.split(",").slice(0, 2).join(","));
    setOpen(false);
    setResults([]);
    setTimeout(() => {
      markerRef.current?.remove();
      markerRef.current = null;
    }, 8000);
  };

  useEffect(
    () => () => {
      markerRef.current?.remove();
    },
    []
  );

  return (
    <div
      style={{
        margin: "8px 0 0 4px",
        width: summary ? "auto" : 280,
        maxWidth: "calc(100vw - 24px)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: "white",
          border: "2px solid rgba(21,53,104,0.3)",
          borderRadius: 999,
          padding: summary ? "7px 10px 7px 14px" : "8px 14px",
          boxShadow: "0 2px 8px rgba(21,53,104,0.16)",
          gap: 8,
        }}
      >
        {summary && (
          <>
            <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              {summary}
            </div>
            <span
              style={{
                width: 1,
                height: 18,
                background: "rgba(0,0,0,0.12)",
                flexShrink: 0,
                margin: "0 2px",
              }}
            />
          </>
        )}
        {loading ? (
          <Loader2 size={16} className="animate-spin" style={{ color: "#9ca3af", flexShrink: 0 }} />
        ) : (
          <button
            type="button"
            onClick={() => runSearch()}
            title="Search"
            aria-label="Search"
            style={{
              lineHeight: 0,
              background: promptSearch ? "#153568" : "none",
              border: "none",
              borderRadius: 999,
              padding: promptSearch ? 4 : 0,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <Search size={promptSearch ? 13 : 16} style={{ color: promptSearch ? "#fff" : "#153568" }} />
          </button>
        )}
        <input
          className="jg-map-search-input"
          value={query}
          onChange={(e) => search(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              runSearch(e.target.value);
            }
          }}
          onFocus={() => (results.length > 0 || promptSearch) && setOpen(true)}
          placeholder="Search location…"
          style={{
            flex: summary ? "1 1 190px" : 1,
            border: "none",
            outline: "none",
            fontSize: 14,
            background: "transparent",
            color: "#111827",
            minWidth: 0,
          }}
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setResults([]);
              setOpen(false);
              markerRef.current?.remove();
            }}
            style={{
              color: "#9ca3af",
              cursor: "pointer",
              lineHeight: 0,
              background: "none",
              border: "none",
              padding: 0,
            }}
          >
            <X size={13} />
          </button>
        )}
      </div>

      {open && promptSearch && (
        <div
          style={{
            marginTop: 3,
            background: "white",
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.13)",
            overflow: "hidden",
          }}
        >
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              runSearch();
            }}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "8px 10px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              background: "#eff6ff",
              color: "#1d4ed8",
              display: "flex",
              alignItems: "center",
              gap: 6,
              border: "none",
            }}
          >
            <Search size={12} style={{ flexShrink: 0 }} />
            Click here to search for “{query.trim()}”
          </button>
        </div>
      )}

      {open && !promptSearch && results.length > 0 && (
        <div
          style={{
            marginTop: 3,
            background: "white",
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.13)",
            overflow: "hidden",
          }}
        >
          {results.map((r, i) => (
            <button
              key={r.id || i}
              onMouseDown={() => select(r)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "7px 10px",
                fontSize: 11,
                borderBottom:
                  i < results.length - 1 ? "1px solid #f3f4f6" : "none",
                cursor: "pointer",
                background: "white",
                color: "#1f2937",
                display: "flex",
                alignItems: "flex-start",
                gap: 5,
                border: "none",
              }}
            >
              <Search size={10} style={{ marginTop: 2, color: "#9ca3af", flexShrink: 0 }} />
              <span
                style={{
                  lineHeight: 1.4,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {r.display_name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchControl({ onSearchSelect, top = "10px", center = false, summary = null, keepZoom = false, provider }) {
  const map = useMap();
  const [container, setContainer] = useState(null);

  useEffect(() => {
    const div = L.DomUtil.create("div");
    div.style.position = "absolute";
    div.style.top = top;
    if (center) {
      // Centred bar (the Maps hub pairs it with a per-tab summary). The class
      // lets CSS drop it back to the left + below the pill on phones, where the
      // section pill is itself centred.
      div.className = "maps-centered-search";
      div.style.left = "50%";
      div.style.transform = "translateX(-50%)";
    } else {
      div.style.left = "48px";
      div.style.transform = "";
    }
    div.style.zIndex = "1200";
    div.style.pointerEvents = "all";
    map.getContainer().appendChild(div);
    L.DomEvent.disableScrollPropagation(div);
    setContainer(div);
    return () => {
      div.remove();
    };
  }, [map, top, center]);

  if (!container) return null;
  return ReactDOM.createPortal(
    <SearchUI map={map} onSearchSelect={onSearchSelect} summary={summary} keepZoom={keepZoom} provider={provider} />,
    container
  );
}

// ── Combined export ─────────────────────────────────────────────────────────
export function MapLayers({
  showSearch = true,
  showLocate = true,
  showZoom = false,
  locateZoom,
  // Maps-hub only: on mount, auto-centre on the user's current location (once
  // per session) and drop a "you are here" marker. Off by default so the many
  // address-picker maps that share this control are unaffected.
  autoLocate = false,
  // Whether the auto-centre also flies the map to the fix. `true` for a blank
  // canvas (Survey / Crowd); My Property passes a predicate so it only recenters
  // when there are no owned properties to keep framed. May be a boolean or a
  // `() => boolean` evaluated when the fix resolves.
  autoLocateRecenter = true,
  // Zoom the auto-centre flies to. 15 frames the neighbourhood; maps showing
  // survey numbers pass 16 so parcels are visible on arrival (they draw from 16).
  autoLocateZoom,
  // Surfaces the (debounced) "fetching your location" state to the caller so it
  // can, e.g., ignore card clicks while a locate is in progress.
  onLocatingChange,
  // Fires with (lat, lng) when the user PRESSES "my location" and a fix lands.
  // Lets a picker map treat that as choosing the spot, not merely viewing it.
  onLocate,
  onSearchSelect,
  searchTop = "10px",
  searchCenter = false,
  searchSummary = null,
  // Pan to a searched place without changing zoom (default: zoom in to ~16).
  searchKeepZoom = false,
  extraOverlays = [],
  activeExtras = [],
  onToggleExtra,
  // Base-layer config (the maps tab uses "Cadastral" + the mobile-style toggle).
  defaultBase,
  baseToggle = false,
  // Maps-hub only: remember the chosen base layer (see mapViewStore).
  persistBase = false,
  // One overlay lifted out of the Overlays popover into the button stack.
  primaryExtra,
  // Maps-hub only: listen for the toolbar search's "fly to a place" broadcast
  // and pan this map there. Off by default so the address-picker maps that share
  // this control aren't driven by the hub toolbar (which never targets them).
  flyOnBroadcast = false,
  // "google" (default) | "open" — see the top of this file. The search box
  // follows it: Google search on a Google map, OpenStreetMap on the open one.
  tiles = "google",
}) {
  return (
    <>
      {flyOnBroadcast && <FlyToOnBroadcast />}
      {/* Mark the place the toolbar search flew to, on every hub tab. */}
      {flyOnBroadcast && <SearchedPlaceDot />}
      <LayerSwitcher
        extraOverlays={extraOverlays}
        activeExtras={activeExtras}
        onToggleExtra={onToggleExtra}
        defaultBase={defaultBase}
        baseToggle={baseToggle}
        persistBase={persistBase}
        primaryExtra={primaryExtra}
        tiles={tiles}
      />
      {showSearch && (
        <SearchControl
          onSearchSelect={onSearchSelect}
          top={searchTop}
          center={searchCenter}
          summary={searchSummary}
          keepZoom={searchKeepZoom}
          provider={tiles === "open" ? "osm" : "google"}
        />
      )}
      {showLocate && (
        <LocateControl
          locateZoom={locateZoom}
          autoLocate={autoLocate}
          autoLocateRecenter={autoLocateRecenter}
          autoLocateZoom={autoLocateZoom}
          onLocatingChange={onLocatingChange}
          onLocate={onLocate}
        />
      )}
      {/* Mounted after locate so it stacks directly above it in the corner. */}
      {showZoom && <ZoomControlBR />}
    </>
  );
}

/**
 * Just a Google base layer (with its logo + credits), for maps that draw their
 * own base switch, e.g. the Listings maps. `mapType`: roadmap | satellite | terrain.
 */
export function GoogleBaseLayer({ mapType = "roadmap" }) {
  const map = useMap();
  useEffect(() => {
    const def = GOOGLE_BASE_TILES.find((t) => t.mapType === mapType) || GOOGLE_BASE_TILES[1];
    const layer = createBaseLayer(def);
    layer.addTo(map);
    return () => {
      layer.remove();
    };
  }, [map, mapType]);
  return <GoogleAttribution mapType={mapType} />;
}

// Thin export for small preview maps that only need the base layer + switcher.
export function MapTileLayers({ tiles = "google" } = {}) {
  return <LayerSwitcher tiles={tiles} />;
}

export default MapLayers;
